import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  collection, onSnapshot, getDocs, getDoc, doc, setDoc, deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { isAdminRole } from '../roles';
import { useModuleAdmin } from '../useModuleAdmin';
import HeaderActionsPortal from './HeaderActionsPortal';
import {
  BookOpen, ShieldCheck, Plus, X, Copy, Pencil, Trash2, Send, CheckCircle2, Search,
} from 'lucide-react';
import {
  SCOPE_STATUS, SCOPE_STATUS_LABEL, SCOPE_STATUS_COLOUR, scopeId, buildScopeLookups, sortScopes,
} from '../examScope';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

/**
 * Exam Scope -- what a form is expected to have revised before each test.
 *
 * One record per school year x assessment x subject x class level. The level, not the class: a
 * panel sets the same scope for the whole form, and asking for it class by class would be four
 * identical records to keep in step.
 *
 * Approval before publication, one stage. Who signs it off is worked out rather than maintained: a
 * subject belongs to a department, a department has a Head and a Deputy in Teaching Duty, and a
 * trigger projects that into metadata/exam_scope_approvers for the rules to read. Nobody keeps a
 * separate list of approvers, so there is no second list to drift.
 *
 * Copy to new exists because a scope repeats: the same term next year covers close to the same
 * ground. Copying opens the form on a fresh record with the wording carried over and the status
 * reset -- last year's approval does not travel with the words.
 */

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-CA')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const card = (isMobile) => ({
  backgroundColor: 'var(--color-bg-card)',
  borderRadius: isMobile ? 0 : 'var(--radius-lg)',
  boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
  overflow: 'hidden',
});

const emptyForm = () => ({
  schoolYear: '', assessmentCode: '', subjectCode: '', classLevel: '', description: '',
});

const ExamScope = () => {
  const { t } = useT();
  // Both are stored on the record; examScope.js keeps the English as the fallback.
  const statusWord = (v) => t(`examScope.statuses.${v}`, {}, SCOPE_STATUS_LABEL[v] || v);
  const actionWord = (v) => t(
    `examScope.actions.${String(v || '').toLowerCase().replace(/[^a-z]/g, '')}`, {}, v,
  );
  const { activeSchoolId, isMobile, userProfile, user, closeSidebar } = useOutletContext();
  const ownEmail = (user?.email || '').toLowerCase();
  const ownName = userProfile?.name || userProfile?.initial || ownEmail;
  const isAdmin = isAdminRole(String(userProfile?.role || '').toLowerCase());
  const isModuleAdmin = useModuleAdmin(activeSchoolId, 'exam_scope');
  const canManage = isAdmin || isModuleAdmin;

  const [activeTab, setActiveTab] = useState('scopes');
  const [scopes, setScopes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [years, setYears] = useState([]);
  const [approvers, setApprovers] = useState({});
  const [loaded, setLoaded] = useState(false);

  const [filter, setFilter] = useState({ year: '', assessment: '', subject: '', level: '', status: '' });
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const look = useMemo(() => buildScopeLookups(settings), [settings]);

  // --- data ------------------------------------------------------------------------------------
  useEffect(() => {
    if (!activeSchoolId) return undefined;
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'))
      .then(snap => setSettings(snap.exists() ? snap.data() : {}))
      .catch(() => setSettings({}));
    getDocs(collection(db, 'schools', activeSchoolId, 'academic_years'))
      .then(snap => {
        const list = [];
        snap.forEach(d => { const y = d.data(); list.push(y.yearName || y.yearId); });
        setYears([...new Set(list.filter(Boolean))].sort().reverse());
      })
      .catch(() => setYears([]));
    // Derived from Teaching Duty by a trigger. Read here only to tell somebody whether they may
    // approve, and -- more usefully -- to say when a department has nobody who can.
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'exam_scope_approvers'))
      .then(snap => setApprovers(snap.exists() ? (snap.data().byDepartment || {}) : {}))
      .catch(() => setApprovers({}));

    return onSnapshot(
      collection(db, 'schools', activeSchoolId, 'exam_scope'),
      snap => {
        const list = [];
        snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
        setScopes(list.sort(sortScopes));
        setLoaded(true);
      },
      e => { console.error('Error loading exam scopes', e); setLoaded(true); },
    );
  }, [activeSchoolId]);

  const iApprove = useCallback((department) => (
    canManage || (approvers[department] || []).includes(ownEmail)
  ), [canManage, approvers, ownEmail]);

  // --- derived ----------------------------------------------------------------------------------
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scopes.filter(s => (
      (!filter.year || s.schoolYear === filter.year)
      && (!filter.assessment || s.assessmentCode === filter.assessment)
      && (!filter.subject || s.subjectCode === filter.subject)
      && (!filter.level || s.classLevel === filter.level)
      && (!filter.status || s.status === filter.status)
      && (!q || `${look.subjectLabel(s.subjectCode)} ${look.assessmentName(s.assessmentCode)} ${s.classLevel} ${s.description || ''}`.toLowerCase().includes(q))
    ));
  }, [scopes, filter, search, look]);

  const myQueue = useMemo(
    () => scopes.filter(s => s.status === SCOPE_STATUS.PENDING && iApprove(s.department)),
    [scopes, iApprove],
  );

  const toggle = (uid) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });

  // --- actions ----------------------------------------------------------------------------------
  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm(), schoolYear: filter.year || years[0] || '' });
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      schoolYear: s.schoolYear || '', assessmentCode: s.assessmentCode || '',
      subjectCode: s.subjectCode || '', classLevel: s.classLevel || '', description: s.description || '',
    });
    setShowForm(true);
  };

  // Carries the wording, not the sign-off. Year and assessment are cleared on purpose: they are the
  // two things that must change, and leaving last year's values in place is how a scope gets filed
  // against the wrong term.
  const copyToNew = (s) => {
    setEditing(null);
    setForm({
      schoolYear: '', assessmentCode: '',
      subjectCode: s.subjectCode || '', classLevel: s.classLevel || '', description: s.description || '',
    });
    setShowForm(true);
  };

  const save = async (submit) => {
    const dept = look.departmentForSubject(form.subjectCode);
    if (!form.schoolYear || !form.assessmentCode || !form.subjectCode || !form.classLevel || !form.description.trim()) return;
    setSaving(true);
    try {
      const uid = editing?.uid || scopeId();
      const status = submit ? SCOPE_STATUS.PENDING : SCOPE_STATUS.DRAFT;
      const base = {
        scopeId: uid,
        schoolYear: form.schoolYear,
        assessmentCode: form.assessmentCode,
        subjectCode: form.subjectCode,
        // Denormalised at write time, because the rule that decides who may approve reads it from
        // the record. It cannot look the subject up: rules get() one document and cannot query.
        department: dept,
        classLevel: form.classLevel,
        description: form.description.trim(),
        status,
      };
      if (editing) {
        // Editing an approved scope is the module admin's job and leaves a trace, the same rule an
        // issued notice follows: once families have read it, "approved" stops meaning "these words
        // were signed off" unless the change is on the record.
        const wasLive = editing.status === SCOPE_STATUS.APPROVED;
        await setDoc(doc(db, 'schools', activeSchoolId, 'exam_scope', uid), {
          ...base,
          status: wasLive ? SCOPE_STATUS.APPROVED : status,
          submittedByEmail: editing.submittedByEmail,
          submittedByName: editing.submittedByName,
          submittedAt: editing.submittedAt,
          approvalLog: [...(editing.approvalLog || []), {
            action: wasLive ? 'edited after publication' : 'edited',
            byEmail: ownEmail, byName: ownName, at: new Date().toISOString(),
          }],
        }, { merge: true });
      } else {
        await setDoc(doc(db, 'schools', activeSchoolId, 'exam_scope', uid), {
          ...base,
          submittedByEmail: ownEmail,
          submittedByName: ownName,
          submittedAt: new Date().toISOString(),
          approvedByEmail: '', approvedByName: '', approvedAt: '',
          approvalLog: [{ action: submit ? 'submitted' : 'drafted', byEmail: ownEmail, byName: ownName, at: new Date().toISOString() }],
        });
      }
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      console.error('Error saving exam scope', e);
      alert(t('examScope.saveFailed', { message: e.message }));
    }
    setSaving(false);
  };

  const decide = async (s, approve) => {
    const note = approve ? '' : (window.prompt(t('examScope.whySendBack')) ?? null);
    if (!approve && note === null) return;
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'exam_scope', s.uid), {
        status: approve ? SCOPE_STATUS.APPROVED : SCOPE_STATUS.REJECTED,
        approvedByEmail: approve ? ownEmail : '',
        approvedByName: approve ? ownName : '',
        approvedAt: approve ? new Date().toISOString() : '',
        approvalLog: [...(s.approvalLog || []), {
          action: approve ? 'approved' : 'sent back',
          byEmail: ownEmail, byName: ownName, at: new Date().toISOString(), note: note || '',
        }],
      }, { merge: true });
    } catch (e) {
      console.error('Error deciding exam scope', e);
      alert(t('examScope.decideFailed', { message: e.message }));
    }
  };

  const submitForApproval = async (s) => {
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'exam_scope', s.uid), {
        status: SCOPE_STATUS.PENDING,
        approvalLog: [...(s.approvalLog || []), { action: 'submitted', byEmail: ownEmail, byName: ownName, at: new Date().toISOString() }],
      }, { merge: true });
    } catch (e) {
      alert(t('examScope.submitFailed', { message: e.message }));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(t('examScope.deleteConfirm', {
      subject: look.subjectLabel(s.subjectCode), level: look.levelName(s.classLevel),
    }))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'exam_scope', s.uid));
    } catch (e) {
      alert(t('examScope.deleteFailed', { message: e.message }));
    }
  };

  useEffect(() => {
    if (showForm && isMobile) closeSidebar?.();
  }, [showForm, isMobile, closeSidebar]);

  // --- render -----------------------------------------------------------------------------------
  const tabs = [
    { id: 'scopes', label: t('examScope.tabs.scopes'), icon: BookOpen },
    { id: 'approve', label: myQueue.length
      ? t('examScope.tabs.toApproveCount', { count: myQueue.length })
      : t('examScope.tabs.toApprove'), icon: ShieldCheck },
  ];

  const formDept = look.departmentForSubject(form.subjectCode);
  const sel = { margin: 0, flex: '1 1 150px', minWidth: 0 };

  const row = (s, showActions) => {
    const open = expanded.has(s.uid);
    const mine = s.submittedByEmail === ownEmail;
    const live = s.status === SCOPE_STATUS.APPROVED;
    return (
      <div key={s.uid} style={{ borderBottom: '1px solid var(--color-border)' }}>
        <button onClick={() => toggle(s.uid)} style={{ width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: isMobile ? '0.7rem 0.85rem' : '0.75rem 1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.92rem', fontWeight: '700' }}>
              {look.subjectLabel(s.subjectCode)} · {look.levelName(s.classLevel)}
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: '0.1rem' }}>
              <span>{s.schoolYear}</span>
              <span>{look.assessmentName(s.assessmentCode)}</span>
              <span style={{ color: SCOPE_STATUS_COLOUR[s.status], fontWeight: '700' }}>
                {statusWord(s.status)}
              </span>
            </div>
          </div>
        </button>

        {open && (
          <div style={{ padding: isMobile ? '0 0.85rem 0.85rem' : '0 1.25rem 1rem' }}>
            <div style={{ fontSize: '0.9rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {s.description}
            </div>

            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.6rem', lineHeight: 1.7 }}>
              {t('examScope.submittedBy', { name: s.submittedByName || s.submittedByEmail })} · {formatWhen(s.submittedAt)}
              {s.approvedAt && <> &nbsp;|&nbsp; {t('examScope.approvedBy', { name: s.approvedByName || s.approvedByEmail })} · {formatWhen(s.approvedAt)}</>}
            </div>

            {(s.approvalLog || []).length > 1 && (
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.4rem', lineHeight: 1.7 }}>
                {s.approvalLog.map((e, i) => (
                  <div key={i}>
                    <strong style={{ color: 'var(--color-text-main)' }}>{actionWord(e.action)}</strong> — {e.byName || e.byEmail} · {formatWhen(e.at)}
                    {e.note ? ` — “${e.note}”` : ''}
                  </div>
                ))}
              </div>
            )}

            {showActions && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.7rem' }}>
                <button className="btn-secondary" onClick={() => copyToNew(s)} style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                  <Copy size={14} /> {t('examScope.copyToNew')}
                </button>
                {(mine ? !live : canManage) && (
                  <button className="btn-secondary" onClick={() => openEdit(s)} style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                    <Pencil size={14} /> {t('examScope.edit')}
                  </button>
                )}
                {mine && (s.status === SCOPE_STATUS.DRAFT || s.status === SCOPE_STATUS.REJECTED) && (
                  <button className="btn-primary" onClick={() => submitForApproval(s)} style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                    <Send size={14} /> {t('examScope.submitForApproval')}
                  </button>
                )}
                {s.status === SCOPE_STATUS.PENDING && iApprove(s.department) && (
                  <>
                    <button className="btn-outline" onClick={() => decide(s, false)} style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                      {t('examScope.sendBack')}
                    </button>
                    <button className="btn-primary" onClick={() => decide(s, true)} style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                      <CheckCircle2 size={14} /> {t('examScope.approveAndPublish')}
                    </button>
                  </>
                )}
                {((mine && !live) || canManage) && (
                  <button onClick={() => remove(s)} title={t('common.delete')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: '0.25rem' }}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`theme-tab ${activeTab === tab.id ? 'active' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>
        <HeaderActionsPortal>
          <button className="btn-primary" onClick={openNew} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
            <Plus size={15} /> {t('examScope.newScope')}
          </button>
        </HeaderActionsPortal>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '0.75rem' : '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {activeTab === 'scopes' && (
          <>
            <div style={{ ...card(isMobile), padding: isMobile ? '0.75rem' : '0.85rem 1.25rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <select className="input-field" value={filter.year} onChange={e => setFilter({ ...filter, year: e.target.value })} style={sel}>
                <option value="">{t('examScope.allYears')}</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className="input-field" value={filter.assessment} onChange={e => setFilter({ ...filter, assessment: e.target.value })} style={sel}>
                <option value="">{t('examScope.allAssessments')}</option>
                {look.assessments.map(a => <option key={a.code} value={a.code}>{a.nameEn || a.name || a.code}</option>)}
              </select>
              <select className="input-field" value={filter.subject} onChange={e => setFilter({ ...filter, subject: e.target.value })} style={sel}>
                <option value="">{t('examScope.allSubjects')}</option>
                {look.subjects.map(x => <option key={x.code} value={x.code}>{look.subjectLabel(x.code)}</option>)}
              </select>
              <select className="input-field" value={filter.level} onChange={e => setFilter({ ...filter, level: e.target.value })} style={sel}>
                <option value="">{t('examScope.allLevels')}</option>
                {look.levels.map(l => <option key={l.code} value={l.code}>{l.nameEn || l.name || l.code}</option>)}
              </select>
              <select className="input-field" value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })} style={sel}>
                <option value="">{t('examScope.anyStatus')}</option>
                {Object.values(SCOPE_STATUS).map(v => <option key={v} value={v}>{SCOPE_STATUS_LABEL[v]}</option>)}
              </select>
              <div style={{ position: 'relative', flex: '1 1 170px', minWidth: 0 }}>
                <Search size={15} style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
                <input className="input-field" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('examScope.searchWording')} style={{ margin: 0, paddingLeft: '2rem' }} />
              </div>
            </div>

            <div style={card(isMobile)}>
              {!loaded ? <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
                : visible.length === 0 ? (
                  <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
                    {scopes.length === 0 ? t('examScope.noScopes') : t('examScope.nothingMatches')}
                  </div>
                ) : visible.map(s => row(s, true))}
            </div>
          </>
        )}

        {activeTab === 'approve' && (
          <div style={card(isMobile)}>
            {myQueue.length === 0 ? (
              <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
                {/* Says which of the two it is. "Nothing to approve" reads the same whether the queue
                    is empty or you were never an approver, and those need different actions. */}
                {canManage || Object.values(approvers).some(list => list.includes(ownEmail))
                  ? t('examScope.nothingWaiting')
                  : t('examScope.notAPanelHead')}
              </div>
            ) : myQueue.map(s => row(s, true))}
          </div>
        )}
      </div>

      {showForm && (
        <div onClick={() => { if (!saving) setShowForm(false); }} style={{ position: 'fixed', inset: 0, zIndex: 120, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '760px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>
                {editing ? t('examScope.editScope') : t('examScope.newScopeTitle')}
              </h3>
              <button type="button" onClick={() => setShowForm(false)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}><X size={20} /></button>
            </div>

            <div style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('examScope.schoolYear')}</label>
                  <select className="input-field" value={form.schoolYear} onChange={e => setForm({ ...form, schoolYear: e.target.value })} style={{ margin: 0 }}>
                    <option value="">— Select —</option>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('examScope.assessment')}</label>
                  <select className="input-field" value={form.assessmentCode} onChange={e => setForm({ ...form, assessmentCode: e.target.value })} style={{ margin: 0 }}>
                    <option value="">— Select —</option>
                    {look.assessments.map(a => <option key={a.code} value={a.code}>{a.nameEn || a.name || a.code}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('examScope.subject')}</label>
                  <select className="input-field" value={form.subjectCode} onChange={e => setForm({ ...form, subjectCode: e.target.value })} style={{ margin: 0 }}>
                    <option value="">— Select —</option>
                    {look.subjects.map(x => <option key={x.code} value={x.code}>{look.subjectLabel(x.code)}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('examScope.classLevel')}</label>
                  <select className="input-field" value={form.classLevel} onChange={e => setForm({ ...form, classLevel: e.target.value })} style={{ margin: 0 }}>
                    <option value="">— Select —</option>
                    {look.levels.map(l => <option key={l.code} value={l.code}>{l.nameEn || l.name || l.code}</option>)}
                  </select>
                </div>
              </div>

              {/* Said at the point it matters. A subject with no department has no panel head, so
                  submitting it would produce a scope only a system administrator could ever
                  publish -- and the author would have no way of knowing why it sat there. */}
              {form.subjectCode && !formDept && (
                <div style={{ padding: '0.7rem 0.9rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', fontSize: '0.82rem', lineHeight: 1.6 }}>
                  {t('examScope.noDepartment')}
                </div>
              )}
              {formDept && (approvers[formDept] || []).length === 0 && (
                <div style={{ padding: '0.7rem 0.9rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', fontSize: '0.82rem', lineHeight: 1.6 }}>
                  {t('examScope.noApprover', { department: look.departmentName(formDept) })}
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('examScope.scope')}</label>
                <textarea className="input-field" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={8} style={{ margin: 0, resize: 'vertical', fontFamily: 'inherit' }} placeholder={t('examScope.scopePlaceholder')} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" className="btn-outline" onClick={() => setShowForm(false)} disabled={saving} style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>{t('common.cancel')}</button>
                {(!editing || editing.status !== SCOPE_STATUS.APPROVED) && (
                  <button type="button" className="btn-secondary" onClick={() => save(false)} disabled={saving} style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>
                    {t('examScope.saveAsDraft')}
                  </button>
                )}
                <button type="button" className="btn-primary" onClick={() => save(true)} disabled={saving} style={{ width: 'auto', padding: '0.55rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Send size={15} />
                  {saving ? t('common.saving') : (editing?.status === SCOPE_STATUS.APPROVED ? t('examScope.saveChanges') : t('examScope.submitForApproval'))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamScope;
